import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  NavigateNext as NextPageIcon,
  NavigateBefore as PrevPageIcon,
} from "@material-ui/icons";
import {
  FormControl,
  Select,
  MenuItem,
  Button as MuiButton,
  IconButton,
  Typography,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
// Removed unused imports to keep the file lean and consistent
import React, { useState, memo, useEffect } from "react";

// Define styles using Material-UI's styling system, aligned with Material/Google guidelines
const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "Poppins, sans-serif",
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0.5, 0),
    minHeight: 36,
    height: 36,
    "& .MuiTypography-root": {
      fontFamily: "Poppins, sans-serif",
    },
    "& .MuiButton-root": {
      fontFamily: "Poppins, sans-serif",
    },
  },
  inner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  goToGroup: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(2),
  },
  navIconButton: {
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    padding: 6,
    minWidth: 28,
    minHeight: 28,
    width: 28,
    height: 28,
    transform: "scale(1)",
    color: theme.palette.text.secondary,
    borderRadius: "50%",
    "&:hover": {
      transform: "scale(1.12)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.18)",
      backgroundColor: theme.palette.action.hover,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
    "&.Mui-disabled": {
      color: theme.palette.action.disabled,
    },
  },
  pageNumberButton: {
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    minWidth: 28,
    width: 28,
    height: 28,
    margin: "0 2px",
    padding: 0,
    borderRadius: "50%",
    fontWeight: 500,
    fontSize: 12,
    textTransform: "none",
    color: theme.palette.text.secondary,
    "&:hover:not([variant='contained'])": {
      transform: "scale(1.08)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  activePageButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  goToPageInput: {
    marginRight: -1,
    zIndex: 1,
  },
  goToPageTextField: {
    width: "60px",
    height: 28,
    fontSize: 12,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.background.paper,
  },
  goToPageButton: {
    height: 28,
    minWidth: 36,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "none",
    fontWeight: 500,
    fontSize: 12,
    boxShadow: "none",
    position: "relative",
    zIndex: 0,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: "none",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  rowsPerPageLabel: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  rowsPerPageSelect: {
    minWidth: 56,
    backgroundColor: theme.palette.action.hover,
    borderRadius: 4,
    fontSize: 12,
    height: 28,
  },
  rangeText: {
    margin: theme.spacing(0, 1.5),
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
}));

const CustomPagination = (props) => {
  const {
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions,
  } = props;
  const classes = useStyles();
  const [goToPage, setGoToPage] = useState("");
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  useEffect(() => {
    setGoToPage((page + 1).toString());
  }, [page]);

  // const pageNumbers = [];

  // // Calculate visible page numbers
  // let startPage = Math.max(0, page - 2);
  // let endPage = Math.min(totalPages - 1, page + 2);

  // // Ensure we always show 5 page numbers if available
  // if (endPage - startPage < 4 && totalPages > 4) {
  //   if (startPage === 0) {
  //     endPage = Math.min(4, totalPages - 1);
  //   } else if (endPage === totalPages - 1) {
  //     startPage = Math.max(0, totalPages - 5);
  //   }
  // }

  // for (let i = startPage; i <= endPage; i++) {
  //   pageNumbers.push(i);
  // }

  const handlePageSizeChange = (event) => {
    onPageSizeChange(parseInt(event.target.value, 10));
  };

  const handleGoToPageChange = (event) => {
    setGoToPage(event.target.value);
  };

  const handleGoToPageSubmit = () => {
    const pageNum = parseInt(goToPage, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum - 1); // Convert from 1-based (UI) to 0-based (internal)
      // No need to reset goToPage as it will be updated via the useEffect
    } else {
      // Reset invalid input to current page
      setGoToPage((page + 1).toString());
    }
  };

  const handleGoToPageKeyPress = (event) => {
    if (event.key === "Enter") {
      handleGoToPageSubmit();
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.inner}>
        {/* Go to page section FIRST */}
        <div className={classes.goToGroup}>
          <TextField
            label="Page"
            value={goToPage}
            onChange={handleGoToPageChange}
            onKeyDown={handleGoToPageKeyPress}
            variant="outlined"
            size="small"
            type="number"
            placeholder=""
            className={classes.goToPageInput}
            InputProps={{
              inputProps: {
                min: 1,
                max: totalPages,
                style: { padding: "4px 2px", fontSize: 12 },
              },
              className: classes.goToPageTextField,
            }}
            InputLabelProps={{
              style: {
                fontSize: 12,
                backgroundColor: "transparent",
                paddingLeft: 2,
                paddingRight: 2,
              },
              shrink: true,
            }}
          />
          <MuiButton
            onClick={handleGoToPageSubmit}
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            className={classes.goToPageButton}
            aria-label="Go to page"
          >
            Go
          </MuiButton>
        </div>

        {/* Rows per page selector */}
        <Typography variant="body2" className={classes.rowsPerPageLabel}>
          Rows per page:
        </Typography>

        <FormControl variant="filled" size="small">
          <Select
            value={rowsPerPage}
            onChange={handlePageSizeChange}
            disableUnderline
            className={classes.rowsPerPageSelect}
            style={{ fontSize: 12 }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option} style={{ fontSize: 12 }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Range display */}
        <Typography variant="body2" className={classes.rangeText}>
          {`${page * rowsPerPage + 1}-${Math.min(
            (page + 1) * rowsPerPage,
            totalCount
          )} of ${totalCount}`}
        </Typography>

        {/* Pagination controls */}
        <Tooltip title="First Page" arrow>
          <span>
            <IconButton
              onClick={() => onPageChange(0)}
              disabled={page === 0}
              className={classes.navIconButton}
              aria-label="First page"
            >
              <FirstPageIcon style={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Previous Page" arrow>
          <span>
            <IconButton
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              className={classes.navIconButton}
              aria-label="Previous page"
            >
              <PrevPageIcon style={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>

        {/* Page numbers

        <div style={{ margin: "0 4px" }}>
          {pageNumbers.map((pageNum) => (
            <MuiButton
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              variant={pageNum === page ? "contained" : "text"}
              disableElevation
              size="small"
              className={`${classes.pageNumberButton} ${
                pageNum === page ? classes.activePageButton : ""
              }`}
            >
              {pageNum + 1}
            </MuiButton>
          ))}
        </div> */}

        <Tooltip title="Next Page" arrow>
          <span>
            <IconButton
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
              className={classes.navIconButton}
              aria-label="Next page"
            >
              <NextPageIcon style={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Last Page" arrow>
          <span>
            <IconButton
              onClick={() => onPageChange(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className={classes.navIconButton}
              aria-label="Last page"
            >
              <LastPageIcon style={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default memo(CustomPagination);
